import React, { useEffect, useState } from 'react'
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CListGroup,
  CListGroupItem,
  CButton,
  CLoadingButton,
  CBadge,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilBolt, cilCheck, cilChevronRight, cilX } from '@coreui/icons'
import { useNavigate } from 'react-router-dom'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import Loader from 'src/components/Loader'

const ChecklistItem = ({ label, done, onClick }) => (
  <CListGroupItem
    className="d-flex align-items-center"
    style={{ cursor: done ? 'default' : 'pointer' }}
    onClick={!done ? onClick : undefined}
  >
    <span
      className={`d-inline-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0 ${
        done ? 'bg-success' : 'bg-danger bg-opacity-25'
      }`}
      style={{ width: 26, height: 26 }}
    >
      <CIcon
        icon={done ? cilCheck : cilX}
        className={done ? 'text-white' : 'text-danger'}
        size="sm"
      />
    </span>
    <span className={`flex-grow-1 ${done ? '' : 'text-medium-emphasis'}`}>{label}</span>
    {!done && <CIcon icon={cilChevronRight} className="text-medium-emphasis" />}
  </CListGroupItem>
)

const ActivateGameModal = ({ visible, onClose, onActivated }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [gameInfo, setGameInfo] = useState({})
  const [roundInfo, setRoundInfo] = useState({})
  const [activationInfo, setActivationInfo] = useState({})

  const fetchInfo = () => {
    setLoading(true)
    CommonService.getGameActivateInfo(1)
      .then((res) => {
        if (res.status === 200) {
          setGameInfo(res.game_info || {})
          setRoundInfo(res.round_info)
          setActivationInfo(res.getGameActivationInfo || {})
        }
      })
      .catch((e) => {
        console.log('E=> ', e)
        ToastComponent('Something went wrong. Please try again.', 'error')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (visible) {
      fetchInfo()
    }
  }, [visible])

  const goTo = (route) => {
    onClose()
    navigate(route)
  }

  const checklist = [
    {
      key: 'club_name',
      label: 'Create Club/League Name',
      done: !!gameInfo?.game_name,
      route: '/club-about-game',
    },
    {
      key: 'game_name',
      label: 'Create Game Name',
      done: !!gameInfo?.game_name,
      route: '/club-about-game',
    },
    {
      key: 'timezone',
      label: 'Select Timezone',
      done: !!gameInfo?.timezone,
      route: '/club-about-game',
    },
    { key: 'round', label: 'Create 1 Round', done: !!roundInfo, route: '/rounds' },
    {
      key: 'pricing',
      label: 'Pricing Plan Paid',
      done: activationInfo?.is_paid === 1,
      route: '/pricing-plan',
    },
  ]

  const completedCount = checklist.filter((item) => item.done).length
  const disabledBtn = !(gameInfo?.game_name && gameInfo?.timezone && roundInfo && activationInfo)
  const isActive = gameInfo?.game_status === 1

  const handleToggleActivation = () => {
    setLoader(true)
    const data = { gameId: gameInfo?.id, gameStatus: isActive ? 0 : 1 }
    CommonService.activateGame(data)
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
          fetchInfo()
          onActivated && onActivated()
        }
      })
      .catch((e) => {
        console.log('E=> ', e)
        ToastComponent('Something went wrong. Please try again.', 'error')
      })
      .finally(() => setLoader(false))
  }

  return (
    <CModal size="lg" visible={visible} onClose={onClose} alignment="center">
      <CModalHeader>
        <CModalTitle>
          <CIcon icon={cilBolt} className="me-2 text-warning" />
          {isActive ? 'Manage Game Activation' : 'Activate Game'}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <p className="text-medium-emphasis mb-0">
                Minimum pre-requisites / conditions to activate your game.
              </p>
              <CBadge
                color={completedCount === checklist.length ? 'success' : 'secondary'}
                shape="rounded-pill"
              >
                {completedCount}/{checklist.length} Complete
              </CBadge>
            </div>
            <CListGroup>
              {checklist.map((item) => (
                <ChecklistItem
                  key={item.key}
                  label={item.label}
                  done={item.done}
                  onClick={() => goTo(item.route)}
                />
              ))}
            </CListGroup>
            {isActive && (
              <CBadge color="success" className="mt-3">
                This game is currently active and visible to members.
              </CBadge>
            )}
          </>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Close
        </CButton>
        {isActive ? (
          <CLoadingButton color="danger" loading={loader} onClick={handleToggleActivation}>
            Deactivate Game
          </CLoadingButton>
        ) : (
          <CLoadingButton
            color="success"
            loading={loader}
            disabled={disabledBtn || loading}
            onClick={handleToggleActivation}
          >
            Activate Game
          </CLoadingButton>
        )}
      </CModalFooter>
    </CModal>
  )
}

export default ActivateGameModal
